<template>
  <PermissionGuard 
    :any-permissions="['ACCESS_PHASES', 'ACCESS_TASKS', 'ACCESS_INTERVENANTS']" 
    :project-id="projectId"
  >
    <div class="project-workspace">
    <!-- Header with Stats -->
    <div class="workspace-header">
      <div class="header-content">
        <div class="header-left">
          <p class="workspace-subtitle">{{ $t('workspace.subtitle') }}</p>
        </div>
        
        <!-- Quick Add Dropdown -->
        <div class="quick-add-dropdown" ref="quickAddDropdown">
          <PermissionGuard :any-permissions="['CREATE_PHASE', 'CREATE_INTERVENANT', 'CREATE_TASK']" :project-id="projectId">
            <button class="btn btn-primary" @click="toggleQuickAdd">
              <span class="btn-icon">➕</span>
              {{ $t('workspace.actions.quickAdd') }}
              <span class="dropdown-arrow">▼</span>
            </button>
          </PermissionGuard>
          
          <div v-if="showQuickAdd" class="dropdown-content">
            <PermissionGuard v-if="project?.multiPhase" permission="CREATE_PHASE" :project-id="projectId">
              <div class="dropdown-item" @click="openPhaseModal">
                <span class="item-icon">📊</span>
                <span>{{ $t('workspace.actions.newPhase') }}</span>
              </div>
            </PermissionGuard>
            
            <PermissionGuard permission="CREATE_INTERVENANT" :project-id="projectId">
              <div class="dropdown-item" @click="openIntervenantModal">
                <span class="item-icon">👤</span>
                <span>{{ $t('workspace.actions.newIntervenant') }}</span>
              </div>
            </PermissionGuard>
            
            <PermissionGuard permission="CREATE_TASK" :project-id="projectId">
              <div class="dropdown-item" @click="openTaskModal">
                <span class="item-icon">✅</span>
                <span>{{ $t('workspace.actions.newTask') }}</span>
              </div>
            </PermissionGuard>
          </div>
        </div>
      </div>

      <!-- Quick Stats Cards -->
      <div class="stats-grid">
        <div v-if="project?.multiPhase" class="stat-card" @click="switchTab('phases')">
          <div class="stat-icon">📊</div>
          <div class="stat-content">
            <div class="stat-value">{{ phases.length }}</div>
            <div class="stat-label">{{ $t('workspace.stats.phases') }}</div>
            <div class="stat-sublabel">{{ phasesSummary }}</div>
          </div>
        </div>
        <div class="stat-card" @click="switchTab('intervenants')">
          <div class="stat-icon">👥</div>
          <div class="stat-content">
            <div class="stat-value">{{ intervenants.length }}</div>
            <div class="stat-label">{{ $t('workspace.stats.intervenants') }}</div>
            <div class="stat-sublabel">{{ intervenantsSummary }}</div>
          </div>
        </div>
        <div class="stat-card" @click="switchTab('tasks')">
          <div class="stat-icon">✅</div>
          <div class="stat-content">
            <div class="stat-value">{{ tasks.length }}</div>
            <div class="stat-label">{{ $t('workspace.stats.tasks') }}</div>
            <div class="stat-sublabel">{{ tasksSummary }}</div>
          </div>
        </div>
        <div v-if="!hideBudgetFromClient" class="stat-card">
          <div class="stat-icon">💰</div>
          <div class="stat-content">
            <div class="stat-value">{{ budgetPercentage }}%</div>
            <div class="stat-label">{{ $t('workspace.stats.budgetUsed') }}</div>
            <div class="stat-sublabel">{{ budgetSummary }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs and Content -->
    <div class="workspace-body">
      <!-- Tabs Navigation -->
      <div class="tabs-navigation">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          :class="['tab-button', { active: activeTab === tab.id }]"
          @click="switchTab(tab.id)"
        >
          <span class="tab-icon" v-html="tab.icon"></span>
          <span class="tab-label">{{ tab.label }}</span>
          <span v-if="tab.count !== undefined" class="tab-badge">{{ tab.count }}</span>
        </button>
      </div>

      <!-- Filter Panel -->
      <Transition name="slide-down">
        <div v-if="showFilters" class="filter-panel">
          <div class="filter-panel-header">
            <h3>{{ $t('workspace.filters.title') }}</h3>
            <button v-if="hasActiveFilters" class="btn-text" @click="clearFilters">
              {{ $t('workspace.filters.clearAll') }}
            </button>
          </div>

          <div class="filter-grid">
            <!-- Status Filter -->
            <div class="filter-group">
              <label class="filter-label">{{ $t('workspace.filters.status') }}</label>
              <div class="filter-options">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    :checked="filters.status.includes('NOT_STARTED')"
                    @change="toggleStatusFilter('NOT_STARTED')"
                  />
                  <span>{{ $t('workspace.filters.statuses.notStarted') }}</span>
                </label>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    :checked="filters.status.includes('IN_PROGRESS')"
                    @change="toggleStatusFilter('IN_PROGRESS')"
                  />
                  <span>{{ $t('workspace.filters.statuses.inProgress') }}</span>
                </label>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    :checked="filters.status.includes('COMPLETED')"
                    @change="toggleStatusFilter('COMPLETED')"
                  />
                  <span>{{ $t('workspace.filters.statuses.completed') }}</span>
                </label>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    :checked="filters.status.includes('ON_HOLD')"
                    @change="toggleStatusFilter('ON_HOLD')"
                  />
                  <span>{{ $t('workspace.filters.statuses.onHold') }}</span>
                </label>
              </div>
            </div>

            <!-- Date Range Filter -->
            <div class="filter-group">
              <label class="filter-label">{{ $t('workspace.filters.dateRange') }}</label>
              <div class="date-range-inputs">
                <input
                  v-model="filters.dateRange.start"
                  type="date"
                  :placeholder="$t('workspace.filters.startDate')"
                  class="filter-input"
                />
                <span class="range-separator">{{ $t('workspace.filters.to') }}</span>
                <input
                  v-model="filters.dateRange.end"
                  type="date"
                  :placeholder="$t('workspace.filters.endDate')"
                  class="filter-input"
                />
              </div>
            </div>

            <!-- Budget Range Filter (for Tasks tab) -->
            <div v-if="activeTab === 'tasks'" class="filter-group">
              <label class="filter-label">{{ $t('workspace.filters.budgetRange') }}</label>
              <div class="budget-range-inputs">
                <input
                  v-model.number="filters.budgetRange.min"
                  type="number"
                  :placeholder="$t('workspace.filters.minAmount')"
                  class="filter-input"
                  min="0"
                />
                <span class="range-separator">{{ $t('workspace.filters.to') }}</span>
                <input
                  v-model.number="filters.budgetRange.max"
                  type="number"
                  :placeholder="$t('workspace.filters.maxAmount')"
                  class="filter-input"
                  min="0"
                />
              </div>
            </div>

            <!-- Assignment Filter (for Tasks tab) -->
            <div v-if="activeTab === 'tasks'" class="filter-group">
              <label class="filter-label">{{ $t('workspace.filters.assignment') }}</label>
              <div class="filter-options">
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    v-model="filters.assignment.assigned"
                  />
                  <span>{{ $t('workspace.filters.assigned') }}</span>
                </label>
                <label class="checkbox-label">
                  <input
                    type="checkbox"
                    v-model="filters.assignment.unassigned"
                  />
                  <span>{{ $t('workspace.filters.unassigned') }}</span>
                </label>
              </div>
            </div>

            <!-- Phase Filter (for Tasks tab) -->
            <div v-if="activeTab === 'tasks'" class="filter-group">
              <label class="filter-label">{{ $t('workspace.filters.phase') }}</label>
              <select v-model="filters.phaseId" class="filter-select">
                <option :value="null">{{ $t('workspace.filters.allPhases') }}</option>
                <option v-for="phase in phases" :key="phase.id" :value="phase.id">
                  {{ phase.name }}
                </option>
              </select>
            </div>

            <!-- Intervenant Filter (for Tasks tab) -->
            <div v-if="activeTab === 'tasks'" class="filter-group">
              <label class="filter-label">{{ $t('workspace.filters.intervenant') }}</label>
              <select v-model="filters.intervenantId" class="filter-select">
                <option :value="null">{{ $t('workspace.filters.allIntervenants') }}</option>
                <option v-for="intervenant in intervenants" :key="intervenant.id" :value="intervenant.id">
                  {{ intervenant.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
      </Transition>

      <!-- Tab Content -->
      <div class="tab-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>{{ $t('common.loading') }}</p>
        </div>

        <!-- Phases Tab -->
        <div v-else-if="activeTab === 'phases' && project?.multiPhase">
          <WorkspacePhasesTab
            :project-id="projectId"
            :phases="phases"
            :tasks="tasks"
            :all-intervenants="intervenants"
            :get-phase-progress="getPhaseProgress"
            :get-phase-budget="getPhaseBudget"
            :get-phase-intervenants="getPhaseIntervenants"
            :show-search="true"
            @phase-selected="selectPhase"
            @phase-created="handlePhaseCreated"
            @phase-updated="handlePhaseUpdated"
            @phase-deleted="handlePhaseDeleted"
          />
        </div>

        <!-- Intervenants Tab -->
        <div v-else-if="activeTab === 'intervenants'">
          <WorkspaceIntervenantsTab
            :project-id="projectId"
            :intervenants="intervenants"
            :tasks="tasks"
            :filtered-intervenants="filteredIntervenants"
            :show-search="true"
            :show-filters="true"
            @intervenant-selected="selectIntervenant"
            @intervenant-assigned="loadWorkspaceData"
            @intervenant-unassigned="loadWorkspaceData"
          />
        </div>

        <!-- Tasks Tab -->
        <div v-else-if="activeTab === 'tasks'">
          <WorkspaceTasksTab
            :project-id="projectId"
            :tasks="tasks"
            :all-intervenants="intervenants"
            :phases="phases"
            :project="project"
            :filtered-tasks="filteredTasks"
            :show-search="true"
            :show-filters="true"
            :show-phase-column="project?.multiPhase"
            @task-selected="selectTask"
            @task-created="handleTaskCreated"
            @task-updated="handleTaskUpdated"
            @task-deleted="handleTaskDeleted"
          />
        </div>

        <!-- Overview Tab -->
        <div v-else-if="activeTab === 'overview'" class="overview-dashboard">
          <!-- Alerts & Issues -->
          <div class="alerts-panel">
            <h3 class="section-title">
              <span class="title-icon">⚠️</span>
              {{ $t('workspace.overview.alertsIssues') }}
            </h3>
            <div v-if="hasAlerts" class="alerts-list">
              <div v-if="overdueTasks.length > 0" class="alert-item alert-danger" @click="switchTab('tasks')">
                <span class="alert-icon">🔴</span>
                <span class="alert-text">{{ $t('workspace.overview.overdueTasksCount', { count: overdueTasks.length }) }}</span>
                <span class="alert-action">→</span>
              </div>
              <div v-if="overduePhases.length > 0" class="alert-item alert-warning" @click="switchTab('phases')">
                <span class="alert-icon">🟡</span>
                <span class="alert-text">{{ $t('workspace.overview.phasesBehindSchedule', { count: overduePhases.length }) }}</span>
                <span class="alert-action">→</span>
              </div>
              <div v-if="unassignedTasks.length > 0" class="alert-item alert-info" @click="switchTab('tasks')">
                <span class="alert-icon">ℹ️</span>
                <span class="alert-text">{{ $t('workspace.overview.unassignedTasksCount', { count: unassignedTasks.length }) }}</span>
                <span class="alert-action">→</span>
              </div>
            </div>
            <div v-else class="no-alerts">
              <span class="success-icon">✅</span>
              <span>{{ $t('workspace.overview.noAlerts') }}</span>
            </div>
          </div>

          <!-- Upcoming & Overdue Split View -->
          <div class="deadlines-grid">
            <!-- Upcoming Deadlines -->
            <div class="deadline-section">
              <h3 class="section-title">
                <span class="title-icon">📅</span>
                {{ $t('workspace.overview.upcomingDays') }}
              </h3>
              <div v-if="upcomingDeadlines.length" class="deadline-list">
                <div
                  v-for="item in upcomingDeadlines"
                  :key="`${item.type}-${item.id}`"
                  class="deadline-item"
                  @click="item.type === 'task' ? (switchTab('tasks'), selectTask(item.item)) : (switchTab('phases'), selectPhase(item.item))"
                >
                  <div class="deadline-icon" :class="`icon-${item.type}`">
                    {{ item.type === 'task' ? '✅' : '📊' }}
                  </div>
                  <div class="deadline-content">
                    <div class="deadline-name">{{ item.name }}</div>
                    <div class="deadline-date">
                      <span class="days-badge days-upcoming">{{ $t('workspace.overview.daysRemaining', { count: item.daysRemaining }) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state-small">
                <span>{{ $t('workspace.overview.noUpcoming') }}</span>
              </div>
            </div>

            <!-- Overdue Items -->
            <div class="deadline-section">
              <h3 class="section-title">
                <span class="title-icon">⏰</span>
                {{ $t('workspace.overview.overdueItems') }}
              </h3>
              <div v-if="overdueItems.length" class="deadline-list">
                <div
                  v-for="item in overdueItems"
                  :key="`${item.type}-${item.id}`"
                  class="deadline-item deadline-overdue"
                  @click="item.type === 'task' ? (switchTab('tasks'), selectTask(item.item)) : (switchTab('phases'), selectPhase(item.item))"
                >
                  <div class="deadline-icon" :class="`icon-${item.type}`">
                    {{ item.type === 'task' ? '✅' : '📊' }}
                  </div>
                  <div class="deadline-content">
                    <div class="deadline-name">{{ item.name }}</div>
                    <div class="deadline-date">
                      <span class="days-badge days-overdue">{{ $t('workspace.overview.daysLate', { count: item.daysLate }) }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state-small">
                <span>{{ $t('workspace.overview.noOverdue') }}</span>
              </div>
            </div>
          </div>

          <!-- Budget Overview -->
          <div v-if="!hideBudgetFromClient" class="budget-overview-card">
            <h3 class="section-title">
              <span class="title-icon">💰</span>
              {{ $t('workspace.overview.budgetOverview') }}
            </h3>
            <div class="budget-main">
              <div class="budget-progress">
                <div class="budget-bar">
                  <div 
                    class="budget-fill" 
                    :class="{ 
                      'budget-normal': budgetPercentage <= 75,
                      'budget-warning': budgetPercentage > 75 && budgetPercentage <= 90,
                      'budget-danger': budgetPercentage > 90
                    }"
                    :style="{ width: Math.min(budgetPercentage, 100) + '%' }"
                  ></div>
                </div>
                <div class="budget-info">
                  <span class="budget-percentage">{{ $t('workspace.overview.budgetUsed', { percentage: budgetPercentage }) }}</span>
                  <span class="budget-amount-text">{{ budgetSummary }}</span>
                </div>
              </div>
            </div>
            
            <!-- Budget Breakdown by Phase -->
            <div v-if="project?.multiPhase && budgetBreakdownByPhase.length" class="budget-breakdown">
              <h4 class="breakdown-title">{{ $t('workspace.overview.budgetByPhase') }}</h4>
              <div class="breakdown-list">
                <div
                  v-for="item in budgetBreakdownByPhase.slice(0, 3)"
                  :key="item.phase.id"
                  class="breakdown-item"
                  @click="switchTab('phases'); selectPhase(item.phase)"
                >
                  <span class="breakdown-label">{{ item.phase.name }}</span>
                  <span class="breakdown-value">{{ item.percentage }}%</span>
                </div>
              </div>
            </div>
            <div v-else-if="project?.multiPhase" class="empty-state-small">
              <span>{{ $t('workspace.overview.noBudgetData') }}</span>
            </div>
          </div>

          <!-- Bottom Grid: Top Intervenants & Tasks by Status -->
          <div class="bottom-grid">
            <!-- Top Active Intervenants -->
            <div class="intervenants-card">
              <h3 class="section-title">
                <span class="title-icon">👥</span>
                {{ $t('workspace.overview.topActiveIntervenants') }}
              </h3>
              <div v-if="topActiveIntervenants.length" class="intervenants-list">
                <div
                  v-for="item in topActiveIntervenants"
                  :key="item.intervenant.id"
                  class="intervenant-item"
                  @click="switchTab('intervenants'); selectIntervenant(item.intervenant)"
                >
                  <div class="intervenant-avatar" :style="{ backgroundColor: getAvatarColor(item.intervenant.name!) }">
                    {{ getInitials(item.intervenant.name!) }}
                  </div>
                  <div class="intervenant-info">
                    <div class="intervenant-name">{{ item.intervenant.name }}</div>
                    <div class="intervenant-tasks">{{ $t('workspace.overview.tasksCount', { count: item.count }) }}</div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-state-small">
                <span>{{ $t('workspace.overview.noIntervenants') }}</span>
              </div>
            </div>

            <!-- Tasks by Status -->
            <div class="status-card">
              <h3 class="section-title">
                <span class="title-icon">📊</span>
                {{ $t('workspace.overview.tasksByStatus') }}
              </h3>
              <div class="status-list">
                <div class="status-item" @click="switchTab('tasks')">
                  <div class="status-indicator status-completed"></div>
                  <span class="status-label">{{ $t('tasks.status.COMPLETED') }}</span>
                  <span class="status-count">{{ tasksByStatus.COMPLETED }}</span>
                </div>
                <div class="status-item" @click="switchTab('tasks')">
                  <div class="status-indicator status-in-progress"></div>
                  <span class="status-label">{{ $t('tasks.status.IN_PROGRESS') }}</span>
                  <span class="status-count">{{ tasksByStatus.IN_PROGRESS }}</span>
                </div>
                <div class="status-item" @click="switchTab('tasks')">
                  <div class="status-indicator status-pending"></div>
                  <span class="status-label">{{ $t('tasks.status.PENDING') }}</span>
                  <span class="status-count">{{ tasksByStatus.PENDING }}</span>
                </div>
                <div v-if="tasksByStatus.ON_HOLD > 0" class="status-item" @click="switchTab('tasks')">
                  <div class="status-indicator status-on-hold"></div>
                  <span class="status-label">{{ $t('tasks.status.ON_HOLD') }}</span>
                  <span class="status-count">{{ tasksByStatus.ON_HOLD }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Details Panel (shown when item is selected) -->
      <transition name="slide-left">
        <div v-if="selectedPhase || selectedIntervenant || selectedTask" class="details-panel-overlay" @click.self="clearSelection">
          <div class="details-panel">
            <div class="panel-header">
              <h3>{{ $t('workspace.details.title') }}</h3>
              <button class="btn-close" @click="clearSelection" :title="$t('common.close')">✕</button>
            </div>
            <div class="panel-content">
            <!-- Phase Details -->
            <div v-if="selectedPhase" class="detail-content">
              <h4>{{ selectedPhase.name }}</h4>
              
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">{{ $t('workspace.details.status') }}</span>
                  <span :class="`status-badge status-${selectedPhase.status?.toLowerCase()}`">
                    {{ $t(`phases.statusOptions.${selectedPhase.status}`) }}
                  </span>
                </div>
                
                <div class="detail-item detail-item-full">
                  <span class="detail-label">{{ $t('workspace.details.duration') }}</span>
                  <span class="detail-value">{{ formatDate(selectedPhase.plannedStartDate) }} - {{ formatDate(selectedPhase.plannedEndDate) }}</span>
                </div>
                
                <div v-if="selectedPhase.description" class="detail-item detail-item-full">
                  <span class="detail-label">{{ $t('workspace.details.description') }}</span>
                  <p class="detail-value">{{ selectedPhase.description }}</p>
                </div>
                
                <div class="detail-item detail-item-full">
                  <span class="detail-label">{{ $t('workspace.details.assignedTasks') }}</span>
                  <div class="task-list">
                    <div
                      v-for="task in getTasksForPhase(selectedPhase.id!)"
                      :key="task.id"
                      class="task-item-compact"
                    >
                      <span class="status-dot" :class="`status-${task.status?.toLowerCase()}`"></span>
                      {{ task.name }}
                    </div>
                    <p v-if="!getTasksForPhase(selectedPhase.id!).length" class="no-tasks">
                      {{ $t('workspace.details.noTasks') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Intervenant Details -->
            <div v-if="selectedIntervenant" class="detail-content">
              <div class="intervenant-header">
                <div
                  class="avatar-large"
                  :style="{ backgroundColor: getAvatarColor(selectedIntervenant.name!) }"
                >
                  {{ getInitials(selectedIntervenant.name!) }}
                </div>
                <h4>{{ selectedIntervenant.name }}</h4>
              </div>
              
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">{{ $t('workspace.details.type') }}</span>
                  <span class="type-badge">{{ $t(`intervenants.types.${selectedIntervenant.type}`) }}</span>
                </div>
                
                <div class="detail-item detail-item-full">
                  <span class="detail-label">{{ $t('workspace.details.email') }}</span>
                  <a class="detail-value" :href="`mailto:${selectedIntervenant.email}`">{{ selectedIntervenant.email }}</a>
                </div>
                
                <div class="detail-item detail-item-full">
                  <span class="detail-label">{{ $t('workspace.details.phone') }}</span>
                  <a class="detail-value" :href="`tel:${selectedIntervenant.phone}`">{{ selectedIntervenant.phone }}</a>
                </div>
                
                <div v-if="selectedIntervenant.address" class="detail-item detail-item-full">
                  <span class="detail-label">{{ $t('workspace.details.address') }}</span>
                  <p class="detail-value">{{ selectedIntervenant.address }}</p>
                </div>
                
                <div class="detail-item detail-item-full">
                  <span class="detail-label">{{ $t('workspace.details.assignedTasks') }}</span>
                  <div class="task-list">
                    <div
                      v-for="task in getTasksForIntervenant(selectedIntervenant.id!)"
                      :key="task.id"
                      class="task-item-compact"
                    >
                      <span class="status-dot" :class="`status-${task.status?.toLowerCase()}`"></span>
                      {{ task.name }} - {{ getFormattedAmount(`task-${task.id}`) }}
                    </div>
                    <p v-if="!getTasksForIntervenant(selectedIntervenant.id!).length" class="no-tasks">
                      {{ $t('workspace.details.noTasks') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Task Details -->
            <div v-if="selectedTask" class="detail-content">
              <h4>{{ selectedTask.name }}</h4>
              
              <div class="detail-grid">
                <div class="detail-item">
                  <span class="detail-label">{{ $t('workspace.details.status') }}</span>
                  <span :class="`status-badge status-${selectedTask.status?.toLowerCase()}`">
                    {{ $t(`tasks.status.${selectedTask.status}`) }}
                  </span>
                </div>
                
                <div class="detail-item">
                  <span class="detail-label">{{ $t('workspace.details.amount') }}</span>
                  <span class="amount-value">{{ selectedTask?.id ? getFormattedAmount(`task-${selectedTask.id}`) : '-' }}</span>
                </div>
                
                <div v-if="selectedTask.intervenantName" class="detail-item">
                  <span class="detail-label">{{ $t('workspace.details.intervenant') }}</span>
                  <span class="detail-value">{{ selectedTask.intervenantName }}</span>
                </div>
                
                <div v-if="project?.multiPhase && selectedTask.phaseName" class="detail-item">
                  <span class="detail-label">{{ $t('workspace.details.phase') }}</span>
                  <span class="detail-value">{{ selectedTask.phaseName }}</span>
                </div>
                
                <div class="detail-item detail-item-full">
                  <span class="detail-label">{{ $t('workspace.details.duration') }}</span>
                  <span class="detail-value">{{ formatDate(selectedTask.startDate) }} - {{ formatDate(selectedTask.endDate) }}</span>
                </div>
                
                <div v-if="selectedTask.description" class="detail-item detail-item-full">
                  <span class="detail-label">{{ $t('workspace.details.description') }}</span>
                  <p class="detail-value">{{ selectedTask.description }}</p>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </transition>
    </div>

    <!-- Phase Modal -->
    <PhaseFormModal
      :show="showPhaseModal"
      :phase="editingPhase"
      :project-id="projectId"
      @close="closePhaseModal"
      @saved="handlePhaseSaved"
    />

    <!-- Intervenant Modal -->
    <AssignIntervenantModal
      :show="showIntervenantModal"
      :phase="null"
      :project-id="projectId"
      :existing-intervenants="intervenants"
      @close="closeIntervenantModal"
      @intervenant-assigned="handleIntervenantSaved"
    />

    <!-- Task Modal -->
    <TaskFormModal
      :show="showTaskModal"
      :task="editingTask"
      :project-id="projectId"
      :project="project"
      :phases="phases"
      :intervenants="intervenants"
      @close="closeTaskModal"
      @saved="handleTaskSaved"
    />



    <!-- Delete Confirmation Modal - Phase -->
    <ConfirmModal
      v-if="showDeletePhaseConfirm"
      :isVisible="showDeletePhaseConfirm"
      :title="$t('phases.deletePhase')"
      :message="$t('workspace.confirmDelete.phase')"
      :details="deletePhaseMessage"
      :confirm-text="$t('common.delete')"
      :cancel-text="$t('common.cancel')"
      type="danger"
      @confirm="confirmDeletePhase"
      @cancel="cancelDeletePhase"
    />

    </div>
  </PermissionGuard>
</template>

<script setup lang="ts">
// @ts-ignore
import { ref, computed, onMounted, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useNotifications } from '@/composables/useNotifications'
import { usePermissions } from '@/composables/usePermissions'
import { useAuthStore } from '@/stores'
import PermissionGuard from '@/components/common/PermissionGuard.vue'
import PhaseFormModal from './modals/PhaseFormModal.vue'
import AssignIntervenantModal from './modals/AssignIntervenantModal.vue'
import TaskFormModal from './modals/TaskFormModal.vue'
import ConfirmModal from '@/components/common/ConfirmModal.vue'
import WorkspaceTasksTab from './shared/WorkspaceTasksTab.vue'
import WorkspaceIntervenantsTab from './shared/WorkspaceIntervenantsTab.vue'
import WorkspacePhasesTab from './shared/WorkspacePhasesTab.vue'
import projectPhasesService from '@/services/projectPhases.service'
import intervenantsService from '@/services/intervenants.service'
import projectTasksService from '@/services/projectTasks.service'
import reportService from '@/services/report.service.index'
import projectService from '@/services/project.service'
import { formatCurrencyWithUserPreference } from '@/utils/currencyUtils'
import { formatDate } from '@/utils/formatters'
import { getAvatarColor, getInitials, getProgressClass } from '@/utils/uiHelpers'
import { useProjectPhases } from '@/composables/useProjectPhases'
import type { BudgetConsumptionReport } from '@/types/services'

// Project interface
interface Project {
  id: number
  name: string
  multiPhase?: boolean
}

// Phase interface (based on PhaseDto from backend)
interface Phase {
  id?: number
  projectId: number
  name: string
  description?: string
  plannedStartDate?: string
  plannedEndDate?: string
  actualStartDate?: string
  actualEndDate?: string
  dependencyPhaseId?: number
  dependencyPhaseName?: string
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'CANCELLED'
  isDelivered?: boolean
  deliveredDate?: string
  createdBy?: number
  createdAt?: string
  updatedAt?: string
}

// Intervenant interface (based on IntervenantDto from backend)
interface Intervenant {
  id?: number
  projectId?: number
  name: string
  type?: 'ARCHITECT' | 'STRUCTURAL_ENGINEER' | 'ELECTRICAL_ENGINEER' | 'PLUMBING_ENGINEER' | 
        'MECHANICAL_ENGINEER' | 'CONTRACTOR' | 'SUBCONTRACTOR' | 'SUPPLIER' | 'CONSULTANT' | 
        'SUPERVISOR' | 'INSPECTOR' | 'PROJECT_MANAGER' | 'SITE_MANAGER' | 'FOREMAN' | 
        'TECHNICIAN' | 'WORKER' | 'OTHER'
  email: string
  phone?: string
  address?: string
  createdAt?: string
  updatedAt?: string
}

// Task interface (based on TaskDto from backend)
interface Task {
  id?: number
  projectId?: number
  name: string
  description?: string
  amount: number
  startDate?: string
  endDate?: string
  status?: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD'
  assignedTo?: string
  createdBy?: number
  updatedBy?: number
  createdAt?: string
  updatedAt?: string
  intervenantName?: string
  intervenantId?: number
  phaseId?: number
  phaseName?: string
}

const { t } = useI18n()
const router = useRouter()
const { notifySuccess, notifyError } = useNotifications()
const { hasPermissionForProject, hasAnyPermissionForProject, PermissionType } = usePermissions()
const authStore = useAuthStore()

// Detect if the current user is a CLIENT
const isClientUser = computed(() => authStore.user?.type === 'CLIENT')

// Hide budget stat card and skip budget API call for clients in FULL_MANAGEMENT projects
const hideBudgetFromClient = computed(() =>
  isClientUser.value && project.value?.managementType === 'FULL_MANAGEMENT'
)

const props = defineProps<{
  projectId: number
  initialProject?: Project | null
  defaultActiveTab?: string
}>()

// State
const loading = ref(false)
const activeTab = ref(props.defaultActiveTab || 'phases')
const searchQuery = ref('')

// Initialize default tab based on project type and permissions
const initializeDefaultTab = () => {
  // If no explicit default tab is set, determine based on project type and permissions
  if (!props.defaultActiveTab) {
    if (project.value && !project.value.multiPhase) {
      // For non-multi-phase projects, prioritize intervenants if accessible
      if (canAccessIntervenants.value) {
        activeTab.value = 'intervenants'
      } else if (canAccessTasks.value) {
        activeTab.value = 'tasks'
      } else {
        activeTab.value = 'overview'
      }
    } else {
      // For multi-phase projects, prioritize phases if accessible
      if (canAccessPhases.value) {
        activeTab.value = 'phases'
      } else if (canAccessIntervenants.value) {
        activeTab.value = 'intervenants'
      } else if (canAccessTasks.value) {
        activeTab.value = 'tasks'
      } else {
        activeTab.value = 'overview'
      }
    }
  } else {
    // Check if the provided default tab is accessible, otherwise fallback
    const requestedTab = props.defaultActiveTab
    const availableTabIds = tabs.value.map((tab: any) => tab.id)
    
    if (availableTabIds.includes(requestedTab)) {
      activeTab.value = requestedTab
    } else {
      // Fallback to first available tab
      activeTab.value = availableTabIds[0] || 'overview'
    }
  }
}

// Data
const project = ref<Project | null>(props.initialProject || null)
const phases = ref<Phase[]>([])
const intervenants = ref<Intervenant[]>([])
const tasks = ref<Task[]>([])
const budgetReport = ref<BudgetConsumptionReport | null>(null)

// Initialize project phases composable for multi-phase calculations
const {
  getPhaseTasks,
  getPhaseProgress,
  getPhaseBudget,
  getPhaseIntervenants,
  getActiveIntervenantsCount,
  getTotalBudget,
  getTasksForIntervenant
} = useProjectPhases(tasks, intervenants)

// Selection
const selectedPhase = ref<Phase | null>(null)
const selectedIntervenant = ref<Intervenant | null>(null)
const selectedTask = ref<Task | null>(null)

// Modals
const showPhaseModal = ref(false)
const showIntervenantModal = ref(false)
const showTaskModal = ref(false)
const showDeletePhaseConfirm = ref(false)
const editingPhase = ref<Phase | null>(null)
const editingTask = ref<Task | null>(null)
const deletePhaseId = ref<number | null>(null)
const deletePhaseMessage = ref('')

// UI State
const showQuickAdd = ref(false)
const showFilters = ref(false)
const quickAddDropdown = ref<HTMLElement | null>(null)

// Pagination State
const currentPage = ref(1)
const itemsPerPage = ref(10)
const itemsPerPageOptions = [5, 10, 25, 50, 100]

// Filter State
const filters = ref({
  status: [] as string[],
  dateRange: {
    start: '',
    end: ''
  },
  budgetRange: {
    min: 0,
    max: 0
  },
  assignment: {
    assigned: true,
    unassigned: true
  },
  phaseId: null as number | null,
  intervenantId: null as number | null
})

// Tabs Configuration
const tabs = computed(() => {
  const allTabs = []

  // In FULL_MANAGEMENT projects, clients must not see the intervenants tab
  // (it exposes cost details per intervenant which are opaque to the client)
  const isFullManagement = (project.value as any)?.managementType === 'FULL_MANAGEMENT'
  const hideIntervenantsFromClient = isClientUser.value && isFullManagement
  
  // Phases tab - only for multi-phase projects and users with phase access
  if (project.value?.multiPhase && canAccessPhases.value) {
    allTabs.push({
      id: 'phases',
      label: t('workspace.tabs.phases'),
      icon: '📊',
      count: phases.value.length
    })
  }
  
  // Intervenants tab - only for users with intervenants access
  // Hidden from clients in FULL_MANAGEMENT projects (cost details are opaque)
  if (canAccessIntervenants.value && !hideIntervenantsFromClient) {
    allTabs.push({
      id: 'intervenants',
      label: t('workspace.tabs.intervenants'),
      icon: '👥',
      count: intervenants.value.length
    })
  }
  
  // Tasks tab - only for users with tasks access
  if (canAccessTasks.value) {
    allTabs.push({
      id: 'tasks',
      label: t('workspace.tabs.tasks'),
      icon: '✅',
      count: tasks.value.length
    })
  }
  
  // Overview tab - always available if user has any workspace access (moved to end)
  if (hasWorkspaceAccess.value) {
    allTabs.push({
      id: 'overview',
      label: t('workspace.tabs.overview'),
      icon: '🏠',
      count: undefined
    })
  }
  
  return allTabs
})

// Permission Checks
const hasWorkspaceAccess = computed(() => {
  return hasAnyPermissionForProject([
    PermissionType.ACCESS_PHASES,
    PermissionType.ACCESS_TASKS,
    PermissionType.ACCESS_INTERVENANTS
  ], props.projectId).value
})

const canAccessPhases = computed(() => {
  return hasPermissionForProject(PermissionType.ACCESS_PHASES, props.projectId).value
})

const canAccessTasks = computed(() => {
  return hasPermissionForProject(PermissionType.ACCESS_TASKS, props.projectId).value
})

const canAccessIntervenants = computed(() => {
  return hasPermissionForProject(PermissionType.ACCESS_INTERVENANTS, props.projectId).value
})

// Computed
const searchAndFilteredPhases = computed(() => {
  let result = phases.value

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((p: Phase) =>
      p.name?.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query)
    )
  }

  // Status filter
  if (filters.value.status.length > 0) {
    result = result.filter((p: Phase) =>
      filters.value.status.includes(p.status || '')
    )
  }

  // Date range filter
  if (filters.value.dateRange.start) {
    result = result.filter((p: Phase) => {
      const startDate = p.plannedStartDate ? new Date(p.plannedStartDate) : null
      const filterStart = new Date(filters.value.dateRange.start)
      return startDate && startDate >= filterStart
    })
  }
  if (filters.value.dateRange.end) {
    result = result.filter((p: Phase) => {
      const endDate = p.plannedEndDate ? new Date(p.plannedEndDate) : null
      const filterEnd = new Date(filters.value.dateRange.end)
      return endDate && endDate <= filterEnd
    })
  }

  return result
})

// Computed - Total pages for phases
const totalPagesPhases = computed(() => {
  return Math.ceil(searchAndFilteredPhases.value.length / itemsPerPage.value)
})

// Computed - Total items for phases
const totalItemsPhases = computed(() => {
  return searchAndFilteredPhases.value.length
})

// Computed - Apply pagination to phases
const filteredPhases = computed(() => {
  if (activeTab.value !== 'phases') {
    return searchAndFilteredPhases.value // Return all when not on phases tab
  }
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return searchAndFilteredPhases.value.slice(start, end)
})

const filteredIntervenants = computed(() => {
  let result = intervenants.value

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((i: Intervenant) =>
      i.name?.toLowerCase().includes(query) ||
      i.email?.toLowerCase().includes(query) ||
      i.phone?.toLowerCase().includes(query)
    )
  }

  // Intervenant-specific filter (if set from filters)
  if (filters.value.intervenantId) {
    result = result.filter((i: Intervenant) => i.id === filters.value.intervenantId)
  }

  return result
})

const filteredTasks = computed(() => {
  let result = tasks.value

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter((t: Task) =>
      t.name?.toLowerCase().includes(query) ||
      t.description?.toLowerCase().includes(query) ||
      t.intervenantName?.toLowerCase().includes(query)
    )
  }

  // Status filter
  if (filters.value.status.length > 0) {
    result = result.filter((t: Task) =>
      filters.value.status.includes(t.status || '')
    )
  }

  // Date range filter
  if (filters.value.dateRange.start) {
    result = result.filter((t: Task) => {
      const startDate = t.startDate ? new Date(t.startDate) : null
      const filterStart = new Date(filters.value.dateRange.start)
      return startDate && startDate >= filterStart
    })
  }
  if (filters.value.dateRange.end) {
    result = result.filter((t: Task) => {
      const endDate = t.endDate ? new Date(t.endDate) : null
      const filterEnd = new Date(filters.value.dateRange.end)
      return endDate && endDate <= filterEnd
    })
  }

  // Budget range filter
  if (filters.value.budgetRange.max > 0) {
    result = result.filter((t: Task) => {
      const amount = t.amount || 0
      return amount >= filters.value.budgetRange.min && amount <= filters.value.budgetRange.max
    })
  }

  // Assignment filter
  if (!filters.value.assignment.assigned && !filters.value.assignment.unassigned) {
    // If both are false, show nothing
    return []
  }
  if (!filters.value.assignment.assigned) {
    // Show only unassigned
    result = result.filter((t: Task) => !t.intervenantId && !t.phaseId)
  }
  if (!filters.value.assignment.unassigned) {
    // Show only assigned
    result = result.filter((t: Task) => t.intervenantId || t.phaseId)
  }

  // Phase filter
  if (filters.value.phaseId) {
    result = result.filter((t: Task) => t.phaseId === filters.value.phaseId)
  }

  // Intervenant filter
  if (filters.value.intervenantId) {
    result = result.filter((t: Task) => t.intervenantId === filters.value.intervenantId)
  }

  return result
})

const inProgressCount = computed(() => {
  const phasesInProgress = phases.value.filter((p: Phase) => p.status === 'IN_PROGRESS').length
  const tasksInProgress = tasks.value.filter((t: Task) => t.status === 'IN_PROGRESS').length
  return phasesInProgress + tasksInProgress
})

const activePhases = computed(() =>
  phases.value.filter((p: Phase) => p.status === 'IN_PROGRESS').slice(0, 5)
)

const recentTasks = computed(() =>
  [...tasks.value]
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5)
)

const unassignedTasks = computed(() =>
  tasks.value.filter((t: Task) => !t.phaseId)
)

// Summary computed properties
const phasesSummary = computed(() => {
  const active = phases.value.filter((p: Phase) => p.status === 'IN_PROGRESS').length
  const completed = phases.value.filter((p: Phase) => p.status === 'COMPLETED').length
  return `${active} ${t('workspace.stats.active')}, ${completed} ${t('workspace.stats.completed')}`
})

const intervenantsSummary = computed(() => {
  const activeCount = getActiveIntervenantsCount()
  return `${activeCount} ${t('workspace.stats.activeContributors')}`
})

const tasksSummary = computed(() => {
  const inProgress = tasks.value.filter((t: Task) => t.status === 'IN_PROGRESS').length
  return `${inProgress} ${t('workspace.stats.inProgress')}`
})

const budgetPercentage = computed(() => {
  const total = getTotalBudget()
  const consumed = getConsumedBudget()
  return total > 0 ? Math.round((consumed / total) * 100) : 0
})

const budgetSummary = computed(() => {
  const consumed = getFormattedAmount('consumed-budget')
  const total = getFormattedAmount('total-budget')
  return `${consumed} / ${total}`
})

// Overview Dashboard Computed Properties
const overdueTasks = computed(() => {
  const now = new Date()
  return tasks.value.filter((t: Task) => {
    if (!t.endDate) return false
    const endDate = new Date(t.endDate)
    return endDate < now && t.status !== 'COMPLETED' && t.status !== 'CANCELLED'
  })
})

const overduePhases = computed(() => {
  const now = new Date()
  return phases.value.filter((p: Phase) => {
    if (!p.plannedEndDate) return false
    const endDate = new Date(p.plannedEndDate)
    return endDate < now && p.status !== 'COMPLETED' && p.status !== 'CANCELLED'
  })
})

const upcomingDeadlines = computed(() => {
  const now = new Date()
  const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000)
  
  const upcomingTasks = tasks.value
    .filter((t: Task) => {
      if (!t.endDate || t.status === 'COMPLETED' || t.status === 'CANCELLED') return false
      const endDate = new Date(t.endDate)
      return endDate >= now && endDate <= sevenDaysFromNow
    })
    .map((t: Task) => ({
      type: 'task',
      id: t.id,
      name: t.name,
      endDate: t.endDate,
      daysRemaining: Math.ceil((new Date(t.endDate!).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
      item: t
    }))
  
  const upcomingPhases = phases.value
    .filter((p: Phase) => {
      if (!p.plannedEndDate || p.status === 'COMPLETED' || p.status === 'CANCELLED') return false
      const endDate = new Date(p.plannedEndDate)
      return endDate >= now && endDate <= sevenDaysFromNow
    })
    .map((p: Phase) => ({
      type: 'phase',
      id: p.id,
      name: p.name,
      endDate: p.plannedEndDate,
      daysRemaining: Math.ceil((new Date(p.plannedEndDate!).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)),
      item: p
    }))
  
  return [...upcomingTasks, ...upcomingPhases]
    .sort((a, b) => a.daysRemaining - b.daysRemaining)
    .slice(0, 5)
})

const overdueItems = computed(() => {
  const now = new Date()
  
  const overdueTaskItems = overdueTasks.value.map((t: Task) => ({
    type: 'task',
    id: t.id,
    name: t.name,
    endDate: t.endDate,
    daysLate: Math.ceil((now.getTime() - new Date(t.endDate!).getTime()) / (1000 * 60 * 60 * 24)),
    item: t
  }))
  
  const overduePhaseItems = overduePhases.value.map((p: Phase) => ({
    type: 'phase',
    id: p.id,
    name: p.name,
    endDate: p.plannedEndDate,
    daysLate: Math.ceil((now.getTime() - new Date(p.plannedEndDate!).getTime()) / (1000 * 60 * 60 * 24)),
    item: p
  }))
  
  return [...overdueTaskItems, ...overduePhaseItems]
    .sort((a, b) => b.daysLate - a.daysLate)
    .slice(0, 5)
})

const tasksByStatus = computed(() => {
  const statuses = {
    COMPLETED: 0,
    IN_PROGRESS: 0,
    PENDING: 0,
    ON_HOLD: 0,
    CANCELLED: 0
  }
  
  tasks.value.forEach((t: Task) => {
    const status = t.status || 'PENDING'
    if (status in statuses) {
      statuses[status as keyof typeof statuses]++
    }
  })
  
  return statuses
})

const topActiveIntervenants = computed(() => {
  const intervenantTaskCount = new Map<number, { intervenant: Intervenant; count: number }>()
  
  tasks.value.forEach((t: Task) => {
    if (t.intervenantId && (t.status === 'IN_PROGRESS' || t.status === 'PENDING')) {
      const existing = intervenantTaskCount.get(t.intervenantId)
      if (existing) {
        existing.count++
      } else {
        const intervenant = intervenants.value.find((i: Intervenant) => i.id === t.intervenantId)
        if (intervenant) {
          intervenantTaskCount.set(t.intervenantId, { intervenant, count: 1 })
        }
      }
    }
  })
  
  return Array.from(intervenantTaskCount.values())
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
})

const budgetBreakdownByPhase = computed(() => {
  return phases.value.map((p: Phase) => {
    const phaseTasks = tasks.value.filter((t: Task) => t.phaseId === p.id)
    const phaseBudget = phaseTasks.reduce((sum: number, t: Task) => sum + (t.amount || 0), 0)
    const total = getTotalBudget()
    const percentage = total > 0 ? Math.round((phaseBudget / total) * 100) : 0
    
    return {
      phase: p,
      budget: phaseBudget,
      percentage,
      taskCount: phaseTasks.length
    }
  }).filter((item: any) => item.budget > 0)
    .sort((a: any, b: any) => b.budget - a.budget)
})

const hasAlerts = computed(() => {
  return overdueTasks.value.length > 0 || 
         overduePhases.value.length > 0 || 
         unassignedTasks.value.length > 0
})

// Methods
const loadData = async () => {
  loading.value = true
  try {
    // First, check if user has any workspace access
    if (!hasWorkspaceAccess.value) {
      notifyError(t('workspace.errors.noAccess'))
      return
    }

    // First, check if we need to load project data
    // Only fetch project data if not already loaded
    if (!project.value || project.value.id !== props.projectId) {
      project.value = await projectService.getProjectById(props.projectId.toString())
    }
    
    // Now prepare API calls based on project configuration and permissions
    const apiCalls: Promise<any>[] = []
    const apiCallMap: string[] = []
    
    // Only fetch intervenants if user has permission
    if (canAccessIntervenants.value) {
      apiCalls.push(intervenantsService.getIntervenants(props.projectId.toString()))
      apiCallMap.push('intervenants')
    }
    
    // Only fetch tasks if user has permission
    if (canAccessTasks.value) {
      apiCalls.push(projectTasksService.getTasks(props.projectId.toString()))
      apiCallMap.push('tasks')
    }
    
    // Only fetch phases if the project is multi-phase and user has permission
    if (project.value?.multiPhase && canAccessPhases.value) {
      apiCalls.push(projectPhasesService.getPhases(props.projectId.toString()))
      apiCallMap.push('phases')
    }
    
    // Skip budget report for clients in FULL_MANAGEMENT projects
    if (apiCalls.length > 0 && !hideBudgetFromClient.value) {
      apiCalls.push(reportService.getBudgetConsumptionReport(props.projectId.toString()))
      apiCallMap.push('budget')
    }
    
    // Execute API calls in parallel
    const results = await Promise.all(apiCalls)
    
    // Assign results based on what was fetched
    for (let i = 0; i < results.length; i++) {
      const dataType = apiCallMap[i]
      switch (dataType) {
        case 'intervenants':
          intervenants.value = results[i] || []
          break
        case 'tasks':
          tasks.value = results[i] || []
          break
        case 'phases':
          phases.value = results[i] || []
          break
        case 'budget':
          budgetReport.value = results[i] || null
          break
      }
    }
    
    // Clear data for which user doesn't have permissions
    if (!canAccessIntervenants.value) {
      intervenants.value = []
    }
    if (!canAccessTasks.value) {
      tasks.value = []
    }
    if (!canAccessPhases.value) {
      phases.value = []
    }
    
    // Initialize the default tab based on project type and permissions
    initializeDefaultTab()
    
    // Format all amounts after loading data
    await formatAllAmounts()
  } catch (error) {
    notifyError(t('workspace.errors.loadFailed'))
  } finally {
    loading.value = false
  }
}

// Format all amounts for phases, tasks, and budget summary
const formatAllAmounts = async () => {
  try {
    // Format task amounts
    for (const task of tasks.value) {
      if (task.id && task.amount !== undefined) {
        const key = `task-${task.id}`
        await formatCurrency(task.amount, key)
      }
    }
    
    // Format phase budgets
    for (const phase of phases.value) {
      if (phase.id) {
        const phaseBudget = getPhaseBudget(phase.id)
        const key = `phase-${phase.id}`
        await formatCurrency(phaseBudget, key)
      }
    }
    
    // Format budget summary
    const totalBudget = getTotalBudget()
    const consumedBudget = getConsumedBudget()
    await formatCurrency(totalBudget, 'total-budget')
    await formatCurrency(consumedBudget, 'consumed-budget')
  } catch (error) {
    console.warn('Error formatting amounts:', error)
  }
}

const switchTab = (tabId: string) => {
  activeTab.value = tabId
  currentPage.value = 1 // Reset to page 1 when switching tabs
  clearSelection()
}

// Pagination methods
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPagesPhases.value) {
    currentPage.value = page
  }
}

const nextPage = () => {
  if (currentPage.value < totalPagesPhases.value) {
    currentPage.value++
  }
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const changeItemsPerPage = (value: number) => {
  itemsPerPage.value = value
  currentPage.value = 1 // Reset to first page when changing items per page
}

// Helper to determine which page numbers to show (with ellipsis logic)
const shouldShowPage = (page: number) => {
  if (totalPagesPhases.value <= 7) {
    return true // Show all pages if 7 or fewer
  }
  
  // Always show first page, last page, current page, and adjacent pages
  if (page === 1 || page === totalPagesPhases.value) {
    return true
  }
  
  if (Math.abs(page - currentPage.value) <= 1) {
    return true
  }
  
  return false
}

// Filter Management
const toggleStatusFilter = (status: string) => {
  const index = filters.value.status.indexOf(status)
  if (index > -1) {
    filters.value.status.splice(index, 1)
  } else {
    filters.value.status.push(status)
  }
  currentPage.value = 1 // Reset to page 1 when filter changes
}

const clearFilters = () => {
  filters.value = {
    status: [],
    dateRange: {
      start: '',
      end: ''
    },
    budgetRange: {
      min: 0,
      max: 0
    },
    assignment: {
      assigned: true,
      unassigned: true
    },
    phaseId: null,
    intervenantId: null
  }
  currentPage.value = 1 // Reset to page 1 when clearing filters
}

const hasActiveFilters = computed(() => {
  return filters.value.status.length > 0 ||
    filters.value.dateRange.start !== '' ||
    filters.value.dateRange.end !== '' ||
    filters.value.budgetRange.max > 0 ||
    (!filters.value.assignment.assigned || !filters.value.assignment.unassigned) ||
    filters.value.phaseId !== null ||
    filters.value.intervenantId !== null
})

const activeFilterCount = computed(() => {
  let count = 0
  if (filters.value.status.length > 0) count++
  if (filters.value.dateRange.start || filters.value.dateRange.end) count++
  if (filters.value.budgetRange.max > 0) count++
  if (!filters.value.assignment.assigned || !filters.value.assignment.unassigned) count++
  if (filters.value.phaseId) count++
  if (filters.value.intervenantId) count++
  return count
})

const selectPhase = (phase: any) => {
  selectedPhase.value = phase
  selectedIntervenant.value = null
  selectedTask.value = null
}

const selectIntervenant = (intervenant: any) => {
  selectedIntervenant.value = intervenant
  selectedPhase.value = null
  selectedTask.value = null
}

const selectTask = (task: any) => {
  selectedTask.value = task
  selectedPhase.value = null
  selectedIntervenant.value = null
}

const clearSelection = () => {
  selectedPhase.value = null
  selectedIntervenant.value = null
  selectedTask.value = null
}

// Phase Actions
const editPhase = (phase: any) => {
  if (!hasPermissionForProject(PermissionType.UPDATE_PHASE, props.projectId).value) {
    notifyError(t('workspace.errors.noPermission'))
    return
  }
  editingPhase.value = phase
  showPhaseModal.value = true
}

const configurePhase = (phase: any) => {
  if (!hasPermissionForProject(PermissionType.UPDATE_PHASE, props.projectId).value) {
    notifyError(t('workspace.errors.noPermission'))
    return
  }
  router.push({
    name: 'PhaseConfigurer',
    params: {
      projectId: props.projectId.toString(),
      phaseId: phase.id.toString()
    }
  })
}

const openPhaseWorkspace = (phase: any) => {
  if (!hasPermissionForProject(PermissionType.ACCESS_PHASES, props.projectId).value) {
    notifyError(t('workspace.errors.noPermission'))
    return
  }
  router.push({
    name: 'PhaseWorkspace',
    params: {
      projectId: props.projectId.toString(),
      phaseId: phase.id.toString()
    }
  })
}

const deletePhase = (id: number) => {
  if (!hasPermissionForProject(PermissionType.DELETE_PHASE, props.projectId).value) {
    notifyError(t('workspace.errors.noPermission'))
    return
  }
  const phase = phases.value.find((p: Phase) => p.id === id)
  deletePhaseId.value = id
  deletePhaseMessage.value = phase?.name || ''
  showDeletePhaseConfirm.value = true
}

const confirmDeletePhase = async () => {
  if (deletePhaseId.value === null) return
  
  if (!hasPermissionForProject(PermissionType.DELETE_PHASE, props.projectId).value) {
    notifyError(t('workspace.errors.noPermission'))
    return
  }
  
  try {
    await projectPhasesService.deletePhase(
      props.projectId.toString(), 
      deletePhaseId.value.toString()
    )
    notifySuccess(t('workspace.success.phaseDeleted'))
    await loadData()
  } catch (error) {
    notifyError(t('workspace.errors.phaseDeleteFailed'))
  } finally {
    cancelDeletePhase()
  }
}

const cancelDeletePhase = () => {
  showDeletePhaseConfirm.value = false
  deletePhaseId.value = null
  deletePhaseMessage.value = ''
}

const closePhaseModal = () => {
  showPhaseModal.value = false
  editingPhase.value = null
}

const handlePhaseSaved = () => {
  closePhaseModal()
  loadData()
  // Use generic success message since this handles both create and update
  notifySuccess(editingPhase.value?.id ? t('workspace.success.phaseUpdated') : t('workspace.success.phaseCreated'))
}

const handlePhaseCreated = () => {
  loadData()
  notifySuccess(t('workspace.success.phaseCreated'))
}

const handlePhaseUpdated = () => {
  loadData()
  notifySuccess(t('workspace.success.phaseUpdated'))
}

const handlePhaseDeleted = () => {
  loadData()
  notifySuccess(t('workspace.success.phaseDeleted'))
}

const closeIntervenantModal = () => {
  showIntervenantModal.value = false
}

const handleIntervenantSaved = () => {
  closeIntervenantModal()
  loadData()
}

// Task actions are now handled by WorkspaceTasksTab component
const closeTaskModal = () => {
  showTaskModal.value = false
}

const handleTaskSaved = () => {
  closeTaskModal()
  loadData()
}
const handleTaskCreated = () => {
  loadData()
  notifySuccess(t('workspace.success.taskCreated'))
}

const handleTaskUpdated = () => {
  loadData()
  notifySuccess(t('workspace.success.taskUpdated'))
}

const handleTaskDeleted = () => {
  loadData()
  notifySuccess(t('workspace.success.taskDeleted'))
}

// Helper Methods
const getPhaseTaskCount = (phaseId: number) => {
  return tasks.value.filter((t: Task) => t.phaseId === phaseId).length
}

const getIntervenantTaskCount = (intervenantId: number) => {
  return tasks.value.filter((t: Task) => t.intervenantId === intervenantId).length
}

const getTasksForPhase = (phaseId: number) => {
  return tasks.value.filter((t: Task) => t.phaseId === phaseId)
}

// getTasksForIntervenant is now provided by useProjectPhases composable

// Reactive formatted amounts
const formattedAmounts = ref<Record<string, string>>({})

const formatCurrency = async (amount: number, key?: string) => {
  if (amount === null || amount === undefined) return '-'
  try {
    const formatted = await formatCurrencyWithUserPreference(amount)
    if (key) {
      formattedAmounts.value[key] = formatted
    }
    return formatted
  } catch (err) {
    console.warn('Error formatting currency:', err)
    return amount.toString()
  }
}

const getFormattedAmount = (key: string) => {
  return formattedAmounts.value[key] || '...'
}

// Quick Add Dropdown
const toggleQuickAdd = () => {
  showQuickAdd.value = !showQuickAdd.value
}

const openPhaseModal = () => {
  if (!hasPermissionForProject(PermissionType.CREATE_PHASE, props.projectId).value) {
    notifyError(t('workspace.errors.noPermission'))
    return
  }
  showQuickAdd.value = false
  editingPhase.value = null
  showPhaseModal.value = true
}

const openIntervenantModal = () => {
  if (!hasPermissionForProject(PermissionType.CREATE_INTERVENANT, props.projectId).value) {
    notifyError(t('workspace.errors.noPermission'))
    return
  }
  showQuickAdd.value = false
  showIntervenantModal.value = true
}

const openTaskModal = () => {
  if (!hasPermissionForProject(PermissionType.CREATE_TASK, props.projectId).value) {
    notifyError(t('workspace.errors.noPermission'))
    return
  }
  showQuickAdd.value = false
  showTaskModal.value = true
}

// Progress Calculations - Now handled by useProjectPhases composable
// getPhaseProgress, getPhaseBudget, getPhaseIntervenants, getActiveIntervenantsCount, getTotalBudget
// are now destructured from useProjectPhases

const getConsumedBudget = () => {
  // Use backend calculation if available (based on actual expense transactions)
  if (budgetReport.value && budgetReport.value.totalConsumed !== undefined) {
    return budgetReport.value.totalConsumed
  }
  
  // Fallback to frontend calculation (based on task status)
  return tasks.value
    .filter((t: Task) => t.status === 'COMPLETED' || t.status === 'IN_PROGRESS')
    .reduce((sum: number, task: Task) => sum + (task.amount || 0), 0)
}

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  if (quickAddDropdown.value && !quickAddDropdown.value.contains(event.target as Node)) {
    showQuickAdd.value = false
  }
}

// Close panel when pressing Escape key
const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    if (selectedPhase.value || selectedIntervenant.value || selectedTask.value) {
      clearSelection()
    } else if (showQuickAdd.value) {
      showQuickAdd.value = false
    }
  }
}

// Lifecycle
onMounted(() => {
  loadData()
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscapeKey)
})

watch(() => props.projectId, () => {
  loadData()
})

watch(searchQuery, () => {
  currentPage.value = 1
})

// Cleanup
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscapeKey)
})
</script>

<style scoped>
.project-workspace {
  padding: 1.5rem;
  max-width: 1600px;
  margin: 0 auto;
}

/* Header */
.workspace-header {
  margin-bottom: 2rem;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.header-left {
  flex: 1;
}

.workspace-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--neutral-900);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0 0 0.5rem 0;
}

.workspace-title .icon {
  font-size: 2.5rem;
}

.workspace-subtitle {
  font-size: 1rem;
  color: var(--neutral-600);
  margin: 0;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;
}

@media (max-width: 1200px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s;
  cursor: pointer;
}

.stat-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
  border-color: var(--primary-300);
}

.stat-icon {
  font-size: 2.5rem;
  line-height: 1;
}

.stat-content {
  flex: 1;
  min-width: 0;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--primary-600);
  line-height: 1.2;
}

.stat-label {
  font-size: 0.875rem;
  color: var(--neutral-600);
  margin-top: 0.25rem;
  font-weight: 500;
}

.stat-sublabel {
  font-size: 0.75rem;
  color: var(--neutral-500);
  margin-top: 0.25rem;
}

/* Quick Add Dropdown */
.quick-add-dropdown {
  position: relative;
  display: inline-block;
}

.quick-add-dropdown .btn {
  min-width: 160px;
  justify-content: center;
}

.dropdown-arrow {
  font-size: 0.75rem;
  margin-left: 0.5rem;
  transition: transform 0.2s;
  display: inline-block;
}

.dropdown-content {
  position: absolute;
  right: 0;
  top: calc(100% + 0.5rem);
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 220px;
  padding: 0.5rem;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  color: var(--neutral-700);
  font-weight: 500;
  font-size: 0.875rem;
}

.dropdown-item:hover {
  background: var(--primary-50);
  color: var(--primary-600);
}

.item-icon {
  font-size: 1.25rem;
  line-height: 1;
}

/* Tabs */
.tabs-navigation {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid var(--neutral-200);
  flex-wrap: wrap;
}

.tab-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.875rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  color: var(--neutral-600);
  font-weight: 500;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  margin-bottom: -2px;
  white-space: nowrap;
}

.tab-button:hover {
  color: var(--primary-600);
  background: var(--primary-50);
  border-radius: 8px 8px 0 0;
}

.tab-button.active {
  color: var(--primary-600);
  border-bottom-color: var(--primary-600);
  background: transparent;
}

.tab-icon {
  font-size: 1.125rem;
  line-height: 1;
}

.tab-label {
  font-weight: 600;
}

.tab-badge {
  background: var(--neutral-200);
  color: var(--neutral-700);
  padding: 0.125rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  min-width: 24px;
  text-align: center;
}

.tab-button.active .tab-badge {
  background: var(--primary-600);
  color: white;
}

/* Search Toolbar */
.workspace-toolbar {
  margin-bottom: 1.5rem;
}

.search-box {
  position: relative;
  width: 100%;
  max-width: 400px;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.25rem;
  color: var(--neutral-500);
}

.search-input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  font-size: 0.875rem;
}

.search-input:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

/* Filter Panel */
.filter-panel {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.filter-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.filter-panel-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--neutral-900);
}

.btn-text {
  background: none;
  border: none;
  color: var(--primary-600);
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.15s;
}

.btn-text:hover {
  background: var(--primary-50);
  color: var(--primary-700);
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.filter-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--neutral-700);
}

.filter-options {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: var(--neutral-700);
  padding: 0.375rem;
  border-radius: 6px;
  transition: background 0.15s;
}

.checkbox-label:hover {
  background: var(--neutral-50);
}

.checkbox-label input[type="checkbox"] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.date-range-inputs,
.budget-range-inputs {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.range-separator {
  color: var(--neutral-500);
  font-size: 0.875rem;
}

.filter-input {
  flex: 1;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--neutral-300);
  border-radius: 6px;
  font-size: 0.875rem;
}

.filter-input:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

.filter-select {
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--neutral-300);
  border-radius: 6px;
  font-size: 0.875rem;
  background: white;
  cursor: pointer;
}

.filter-select:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 3px var(--primary-100);
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 0.375rem;
  background: var(--error-500);
  color: white;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.5rem;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  margin-bottom: 0;
}

/* Table */
.workspace-table {
  width: 100%;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border-collapse: collapse;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.workspace-table thead {
  background: var(--neutral-100);
}

.workspace-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--neutral-700);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.workspace-table tbody tr {
  border-bottom: 1px solid var(--neutral-200);
  cursor: pointer;
  transition: background 0.15s;
}

.workspace-table tbody tr:hover {
  background: var(--neutral-50);
}

.workspace-table tbody tr.selected {
  background: var(--primary-50);
}

.workspace-table td {
  padding: 1rem;
  color: var(--neutral-800);
}

.cell-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

/* Status */
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.status-not_started { background: var(--neutral-400); }
.status-dot.status-in_progress { background: var(--primary-500); }
.status-dot.status-completed { background: var(--success-500); }
.status-dot.status-on_hold { background: var(--warning-500); }
.status-dot.status-cancelled { background: var(--error-500); }
.status-dot.status-pending { background: var(--warning-400); }

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-block;
}

.status-badge.status-not_started {
  background: var(--neutral-100);
  color: var(--neutral-700);
}

.status-badge.status-in_progress {
  background: var(--primary-100);
  color: var(--primary-700);
}

.status-badge.status-completed {
  background: var(--success-100);
  color: var(--success-700);
}

.status-badge.status-on_hold {
  background: var(--warning-100);
  color: var(--warning-700);
}

.status-badge.status-cancelled, .status-badge.status-canceled {
  background: var(--error-100);
  color: var(--error-700);
}

.status-badge.status-pending {
  background: var(--warning-100);
  color: var(--warning-700);
}

.type-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  background: var(--neutral-100);
  color: var(--neutral-700);
}

/* Avatar */
.avatar-small {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.avatar-large {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
  margin: 0 auto 1rem;
}

.avatar-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.75rem;
  flex-shrink: 0;
  border: 2px solid white;
}

.avatar-group {
  display: flex;
  align-items: center;
}

.avatar-more {
  background: var(--neutral-400) !important;
  font-size: 0.6875rem;
}

/* Progress Bars */
.progress-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.progress-bar {
  flex: 1;
  height: 8px;
  background: var(--neutral-200);
  border-radius: 4px;
  overflow: hidden;
  min-width: 100px;
}

.progress-fill {
  height: 100%;
  background: var(--success-500);
  transition: width 0.3s ease;
  border-radius: 4px;
}

.progress-fill.progress-success {
  background: var(--success-500);
}

.progress-fill.progress-warning {
  background: var(--warning-500);
}

.progress-fill.progress-info {
  background: var(--primary-500);
}

.progress-text {
  color: var(--neutral-600);
  font-size: 0.875rem;
  font-weight: 600;
  min-width: 40px;
  text-align: right;
}

/* Budget Display */
.budget-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.budget-amount {
  font-weight: 600;
  color: var(--neutral-900);
  font-size: 0.9375rem;
}

/* Task Count Badge */
.task-count-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--neutral-700);
}

.status-icon {
  font-size: 1rem;
}

/* Actions */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-icon-small {
  padding: 0.375rem 0.75rem;
  border: 1px solid var(--neutral-300);
  background: white;
  border-radius: 6px;
  font-size: 0.8125rem;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--neutral-700);
}

.btn-icon-small:hover {
  background: var(--neutral-50);
  border-color: var(--primary-600);
  color: var(--primary-600);
}

.btn-icon-small.btn-danger:hover {
  background: #fef2f2;
  border-color: var(--error-600);
  color: var(--error-600);
}

.task-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  background: var(--primary-100);
  color: var(--primary-700);
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0 0.5rem;
}

.date-range {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.875rem;
}

.date-separator {
  color: var(--neutral-400);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: var(--neutral-500);
}

.empty-icon {
  font-size: 4rem;
  display: block;
  margin-bottom: 1rem;
}

/* Pagination */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--neutral-200);
  background: var(--neutral-50);
  gap: 1rem;
  flex-wrap: wrap;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--neutral-600);
}

.items-per-page {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.items-per-page label {
  font-size: 0.875rem;
  color: var(--neutral-700);
}

.items-select {
  padding: 0.375rem 0.5rem;
  border: 1px solid var(--neutral-300);
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  background: white;
}

.items-select:focus {
  outline: none;
  border-color: var(--primary-500);
  box-shadow: 0 0 0 2px var(--primary-100);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-numbers {
  display: flex;
  gap: 0.25rem;
}

.pagination-btn,
.page-btn {
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.5rem;
  border: 1px solid var(--neutral-300);
  background: white;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--neutral-700);
  font-weight: 500;
}

.pagination-btn:hover:not(:disabled),
.page-btn:hover:not(.active) {
  background: var(--neutral-100);
  border-color: var(--neutral-400);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-btn.active {
  background: var(--primary-500);
  border-color: var(--primary-500);
  color: white;
  font-weight: 600;
}

.pagination-btn {
  font-size: 1.25rem;
  font-weight: 600;
  padding: 0 0.75rem;
}

/* Loading */
.loading-state {
  text-align: center;
  padding: 4rem 2rem;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--neutral-200);
  border-top-color: var(--primary-600);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Overview */
/* Overview Dashboard */
.overview-dashboard {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
}

/* Section Titles */
.section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 1rem 0;
  color: var(--neutral-900);
}

.title-icon {
  font-size: 1.25rem;
}

/* Alerts Panel */
.alerts-panel {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.alerts-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.alert-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
  font-weight: 500;
}

.alert-icon {
  font-size: 1.25rem;
  line-height: 1;
}

.alert-text {
  flex: 1;
}

.alert-action {
  font-size: 1.25rem;
  opacity: 0.5;
  transition: opacity 0.15s;
}

.alert-item:hover .alert-action {
  opacity: 1;
}

.alert-danger {
  background: #fee;
  border: 1px solid #fcc;
  color: #c00;
}

.alert-danger:hover {
  background: #fdd;
  border-color: #fbb;
}

.alert-warning {
  background: #fffbeb;
  border: 1px solid #fef3c7;
  color: #f59e0b;
}

.alert-warning:hover {
  background: #fef3c7;
  border-color: #fde68a;
}

.alert-info {
  background: #eff6ff;
  border: 1px solid #dbeafe;
  color: #3b82f6;
}

.alert-info:hover {
  background: #dbeafe;
  border-color: #bfdbfe;
}

.no-alerts {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1.5rem;
  color: var(--neutral-600);
  font-size: 0.9375rem;
}

.success-icon {
  font-size: 1.5rem;
}

/* Deadlines Grid */
.deadlines-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.deadline-section {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.deadline-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.deadline-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.deadline-item:hover {
  background: var(--neutral-50);
  border-color: var(--primary-300);
  transform: translateX(4px);
}

.deadline-overdue {
  border-color: #fecaca;
  background: #fef2f2;
}

.deadline-overdue:hover {
  background: #fee2e2;
}

.deadline-icon {
  font-size: 1.5rem;
  line-height: 1;
  opacity: 0.8;
}

.deadline-content {
  flex: 1;
  min-width: 0;
}

.deadline-name {
  font-weight: 500;
  color: var(--neutral-900);
  font-size: 0.9375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.deadline-date {
  margin-top: 0.25rem;
}

.days-badge {
  display: inline-block;
  padding: 0.125rem 0.625rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.days-upcoming {
  background: #dbeafe;
  color: #1e40af;
}

.days-overdue {
  background: #fee2e2;
  color: #991b1b;
}

.empty-state-small {
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
  padding: 1.5rem;
}

/* Budget Overview Card */
.budget-overview-card {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.budget-main {
  margin-bottom: 1.5rem;
}

.budget-progress {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.budget-bar {
  width: 100%;
  height: 32px;
  background: var(--neutral-100);
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}

.budget-fill {
  height: 100%;
  transition: width 0.5s ease;
  border-radius: 8px;
}

.budget-normal {
  background: linear-gradient(90deg, #10b981 0%, #34d399 100%);
}

.budget-warning {
  background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%);
}

.budget-danger {
  background: linear-gradient(90deg, #ef4444 0%, #f87171 100%);
}

.budget-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.budget-percentage {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--neutral-900);
}

.budget-amount-text {
  font-size: 0.875rem;
  color: var(--neutral-600);
  font-weight: 500;
}

.budget-breakdown {
  border-top: 1px solid var(--neutral-200);
  padding-top: 1.5rem;
}

.breakdown-title {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--neutral-700);
  margin: 0 0 0.75rem 0;
}

.breakdown-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.625rem;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.breakdown-item:hover {
  background: var(--neutral-50);
}

.breakdown-label {
  font-size: 0.875rem;
  color: var(--neutral-700);
  font-weight: 500;
}

.breakdown-value {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--primary-600);
}

/* Bottom Grid */
.bottom-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

/* Intervenants Card */
.intervenants-card {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.intervenants-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.intervenant-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.intervenant-item:hover {
  background: var(--neutral-50);
  border-color: var(--primary-300);
}

.intervenant-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  flex-shrink: 0;
}

.intervenant-info {
  flex: 1;
  min-width: 0;
}

.intervenant-name {
  font-weight: 600;
  color: var(--neutral-900);
  font-size: 0.9375rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.intervenant-tasks {
  font-size: 0.8125rem;
  color: var(--neutral-600);
  margin-top: 0.125rem;
}

/* Status Card */
.status-card {
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.status-item:hover {
  background: var(--neutral-50);
  border-color: var(--primary-300);
}

.status-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-completed {
  background: #10b981;
}

.status-in-progress {
  background: #3b82f6;
}

.status-pending {
  background: #f59e0b;
}

.status-on-hold {
  background: #6b7280;
}

.status-label {
  flex: 1;
  font-size: 0.9375rem;
  color: var(--neutral-700);
  font-weight: 500;
}

.status-count {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--neutral-900);
}

/* Details Panel */
.details-panel-overlay {
  position: fixed;
  right: 0;
  top: 130px;
  bottom: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 1000;
  display: flex;
  justify-content: flex-end;
}

.details-panel {
  width: 400px;
  max-width: 90vw;
  background: white;
  border-left: 1px solid var(--neutral-200);
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--neutral-200);
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  flex-shrink: 0;
  min-height: 56px;
}

.panel-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  flex: 1;
}

.btn-close {
  background: var(--neutral-100);
  border: 2px solid var(--neutral-300);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.375rem 0.625rem;
  color: var(--neutral-700);
  border-radius: 6px;
  transition: all 0.2s;
  line-height: 1;
  font-weight: 700;
  min-width: 36px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.btn-close:hover {
  background: var(--danger-600);
  border-color: var(--danger-600);
  color: white;
  transform: scale(1.05);
}

.btn-close:active {
  transform: scale(0.95);
}

.panel-content {
  padding: 1rem;
  flex: 1;
  overflow-y: auto;
}

.detail-content h4 {
  margin: 0 0 1rem 0;
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--neutral-900);
}

.intervenant-header {
  text-align: center;
  margin-bottom: 1rem;
}

/* New Grid Layout */
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding: 0.75rem;
  background: var(--neutral-50);
  border-radius: 8px;
  border: 1px solid var(--neutral-200);
}

.detail-item-full {
  grid-column: 1 / -1;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-600);
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.detail-value {
  color: var(--neutral-900);
  font-size: 0.9375rem;
  line-height: 1.4;
  word-break: break-word;
}

.detail-value a {
  color: var(--primary-600);
  text-decoration: none;
}

.detail-value a:hover {
  text-decoration: underline;
}

.detail-item p.detail-value {
  margin: 0;
}

/* Legacy styles for backward compatibility */
.detail-row {
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--neutral-100);
}

.detail-row:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.detail-row p {
  margin: 0.5rem 0 0 0;
  color: var(--neutral-700);
}

.detail-row a {
  color: var(--primary-600);
  text-decoration: none;
}

.detail-row a:hover {
  text-decoration: underline;
}

.amount-value {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--success-600);
}

.task-list {
  margin-top: 0.5rem;
}

.task-item-compact {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  border-radius: 6px;
  margin-bottom: 0.375rem;
  background: white;
  border: 1px solid var(--neutral-200);
  font-size: 0.8125rem;
}

.task-item-compact:last-child {
  margin-bottom: 0;
}

.no-tasks {
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
  padding: 1rem;
  margin: 0;
}

/* Animations */
.slide-left-enter-active,
.slide-left-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.slide-left-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Responsive */
@media (max-width: 1024px) {
  .deadlines-grid,
  .bottom-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .project-workspace {
    padding: 1rem;
  }

  .workspace-title {
    font-size: 1.5rem;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .quick-add-dropdown {
    width: 100%;
  }

  .quick-add-dropdown .btn {
    width: 100%;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .tabs-navigation {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    gap: 0;
  }

  .tab-button {
    white-space: nowrap;
    padding: 0.75rem 1rem;
  }

  .overview-dashboard {
    gap: 1rem;
  }

  .deadlines-grid,
  .bottom-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .details-panel {
    width: 100%;
  }

  .details-panel-overlay {
    justify-content: flex-end;
    align-items: flex-end;
  }

  .details-panel {
    width: 100%;
    height: 65vh;
    border-radius: 16px 16px 0 0;
    border-left: none;
    border-top: 1px solid var(--neutral-200);
  }

  .workspace-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-left,
  .toolbar-right {
    width: 100%;
    min-width: auto;
  }

  .toolbar-right {
    justify-content: stretch;
  }

  .toolbar-right .btn {
    flex: 1;
  }

  .search-box {
    max-width: none;
  }

  .budget-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .pagination-container {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .pagination-controls {
    justify-content: center;
  }

  .dropdown-content {
    min-width: 0;
    width: 100%;
    right: auto;
    left: 0;
  }

  .page-numbers {
    max-width: 100%;
    overflow-x: auto;
  }
}

/* Budget Modal */
.budget-loading {
  text-align: center;
  padding: 3rem 2rem;
}

.budget-modal-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.no-budget-data {
  text-align: center;
  padding: 3rem 2rem;
  color: var(--neutral-500);
}

.no-budget-data p {
  margin: 0;
  font-size: 1rem;
}
</style>
